import React, { useContext, useEffect, useRef, useState } from 'react';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import isEmpty from 'lodash/isEmpty';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import Module from './components/variants/Module';
import { ACTION_BOOK_ONLINE } from './constants';
import { BookingProfileStatus } from '~/constants';
import { analyticsTrackEvent } from '~/core/analytics';
import { CDP_TOUCH_PRIMARY_ACTION } from '~/core/analytics/events';
import withAuthentication from '../../../../core/login/withAuthentication';

import { getLocationStatus, isAsapTelemedEnabledLocation } from '~/core/util/location';
import { LocationContext } from '../../ContextProviders';
import { AccountSummary } from '~/reducers/account';
import { NewBookingState } from '~/reducers/newBooking';
import DynamicLinkOutModal from '~/components/LinkOutModal/DynamicLinkOutModal';
import reasonForVisit from '~/components/ClinicDetailPageFlexible/components/BookModule/components/shared/ReasonForVisit';
import NearbyBookModule from '~/components/ClinicDetailPageFlexible/components/BookModule/components/variants/NearbyBook/NearbyBookModule';
import PartnerNotBookable from '~/components/ClinicDetailPageFlexible/components/BookModule/PartnerNotBookable';

export enum ModuleVariant {
  DEFAULT,
  MODAL,
}

type Props = {
  associatedTelemedLocation?: any;
  newBooking?: any;
  handleRequestAppointmentClick: (
    appointmentTime: number | null | undefined,
    symptoms: string,
    location: LocationInterface,
    newBooking: NewBookingState
  ) => void;
  setBookingProfilePending?: (arg: boolean) => void;
  setBookingProfile?: (accountSummary: AccountSummary, userProfileId: string) => void;
  initialValues?: any;
  className?: string;
  nearbyLocations?: any;
  currentlySelectedLocation?: any;
  setCurrentlySelectedLocation: (value: LocationInterface) => void;
  setNewBookingLocationId: (...args: any[]) => any;
  variant?: ModuleVariant;
  // below props are for old booking flow
  form: formShape;
  autoFocus?: boolean;
  accountSummary?: any;
  login?: any;
  fetchSuggestions?: (...args: any[]) => any;
  suggestions?: any[];
  reasonForVisit?: string;
};

const BookModule = (props: Props) => {
  const {
    currentlySelectedLocation,
    newBooking,
    handleRequestAppointmentClick,
    // below props are for old booking flow
    form,
    setBookingProfilePending,
    setBookingProfile,
    accountSummary,
  } = props;

  const { location, associatedTelemedLocation, referrerOverridesPause, isFromCareTeam } =
    useContext(LocationContext);

  const appointmentDate = newBooking?.booking?.appointmentTime;
  const isLinkOutBookingFlow = !!location.externalBookingUrl;
  const [selectedDate, setSelectedDate] = useState(null);
  const selectedSlot = useRef(appointmentDate ? { appointmentDate } : null);

  const [shouldValidate, setShouldValidate] = useState(true);
  const [errors, setErrors] = useState({});
  const [shouldRenderSuggestions, setShouldRenderSuggestions] = useState(true);
  const [showEmergencyCopy, setShowEmergencyCopy] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(!appointmentDate);
  const [trackingSelectionProperties, setTrackingSelectionProperties] = useState({});
  const [showLinkOutModal, setShowLinkOutModal] = useState(false);

  const enableFormSubmissionIfNoErrors = (errors: any) =>
    isEmpty(errors)
      ? submitButtonDisabled && setSubmitButtonDisabled(false)
      : submitButtonDisabled || setSubmitButtonDisabled(true);

  const locationStatus = getLocationStatus(location, {
    referrerOverridesPause,
    isFromCareTeam,
  });
  const isBookablePartner =
    locationStatus === 'connect' ||
    locationStatus === 'boost' ||
    locationStatus === 'bookablePartner';
  const isNonPartner = locationStatus === 'nonPartner';

  // This is a hacky attempt at trying to handle the asynchronous behavior of the validateFields method.
  // This has been more stable than directly calling validateFields, as there appears to be a race condition
  // on validations setting the submit button to disabled, even though newer calls to validateFields were made.
  useEffect(() => {
    if (shouldValidate) {
      form.validateFields((errors: any) => {
        setErrors(errors);
        enableFormSubmissionIfNoErrors(errors);
        setShouldValidate(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldValidate]);
  const onFieldChange = (value: any, fieldName: any) => {
    if (!shouldRenderSuggestions) setShouldRenderSuggestions(true);
    setErrors({ errors: { ...errors, [fieldName]: false } });
    form.setFieldsValue({ [fieldName]: value });
    setShouldValidate(true);
  };
  const toggleEmergencyCopy = (value: any) => setShowEmergencyCopy(value);
  const onBlur = () => setShouldValidate(true);
  const handleChangeUserProfile = (selectedUserProfile: any) => {
    const clickedUserProfileId = selectedUserProfile.value;
    const clickedUserProfileType = selectedUserProfile.type;
    if (clickedUserProfileType === BookingProfileStatus.PENDING) {
      setBookingProfilePending?.(true);
    } else {
      setBookingProfilePending?.(false);
      setBookingProfile?.(accountSummary, clickedUserProfileId);
    }

    onFieldChange(selectedUserProfile, 'userProfile');
  };
  useEffect(() => {
    if (isAsapTelemedEnabledLocation(location)) {
      setSubmitButtonDisabled(!appointmentDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentDate]);

  const handleSelectSlot = (selected: any, selectionProperties: any) => {
    const slot = Array.isArray(selected) ? selected[0] : selected;
    selectedSlot.current = slot;
    setTrackingSelectionProperties(selectionProperties);

    form.setFieldsValue({ appointmentDate: slot });
    setShouldValidate(true);
  };

  const handleSelectedDate = (selectedDate: any) => setSelectedDate(selectedDate);

  const handleRequestAppointment = () => {
    const appointmentDate = selectedSlot.current?.appointmentDate;
    const reasonForVisit = form.getFieldValue('reasonForVisit');

    if (isLinkOutBookingFlow) {
      setShowLinkOutModal(true);
    } else {
      handleRequestAppointmentClick(
        appointmentDate,
        reasonForVisit,
        currentlySelectedLocation,
        newBooking
      );
    }
  };

  const onSubmit = (e: any) => {
    e && e.preventDefault();
    const { id, uclId } = location;
    const trackingProperties = {
      ...trackingSelectionProperties,
      action: ACTION_BOOK_ONLINE,
      locationId: id,
      uclId,
    };
    analyticsTrackEvent(CDP_TOUCH_PRIMARY_ACTION, trackingProperties);
    handleRequestAppointment();
  };

  const sharedProps = {
    associatedTelemedLocation,
    handleRequestAppointment,
    handleSelectedDate,
    handleSelectSlot,
    location,
    onSubmit,
    selectedDate,
    setSelectedDate,
    selectedSlot: selectedSlot.current,
    trackingSelectionProperties,
    setTrackingSelectionProperties,
    appointmentDate,
    // below props are for old booking flow
    handleChangeUserProfile,
    onBlur,
    onFieldChange,
    toggleEmergencyCopy,
    errors,
    setErrors,
    shouldRenderSuggestions,
    setShouldRenderSuggestions,
    showEmergencyCopy,
    setShowEmergencyCopy,
    submitButtonDisabled,
    setSubmitButtonDisabled,
  };

  if (isNonPartner) {
    return <NearbyBookModule isMobileView={false} />;
  }

  if (isBookablePartner) {
    return (
      <>
        {isLinkOutBookingFlow && (
          <DynamicLinkOutModal
            appointmentTime={selectedSlot.current?.appointmentDate}
            isOpen={showLinkOutModal}
            location={location}
            onClose={() => setShowLinkOutModal(false)}
            reasonForVisit={form.getFieldValue('reasonForVisit')}
          />
        )}
        <Module {...props} {...sharedProps} />
      </>
    );
  }

  return <PartnerNotBookable location={location} />;
};

export default withAuthentication(createForm()(BookModule));
